import React, { memo, useCallback, useMemo } from 'react';
import { View, Image, TouchableOpacity, StyleSheet, Linking, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import type { CorkBoardAttachment } from '@/types/residents/corkBoard';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

function isImageAttachment(attachment: CorkBoardAttachment): boolean {
  return !!attachment.fileType && attachment.fileType.startsWith('image/');
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface AttachmentPreviewProps {
  attachments: CorkBoardAttachment[];
  onImagePress?: (imageUrls: string[], index: number) => void;
  onDocumentPress?: (url: string, title: string) => void;
}

export const AttachmentPreview: React.FC<AttachmentPreviewProps> = memo(({
  attachments,
  onImagePress,
  onDocumentPress,
}) => {
  const { colors } = useThemeColors();

  const imageUrls = useMemo(
    () => (attachments ?? []).filter(isImageAttachment).map((a) => a.fileUrl),
    [attachments],
  );

  const fallbackOpen = useCallback((url: string) => {
    if (Platform.OS === 'web') {
      (globalThis as any).open(url, '_blank');
    } else {
      Linking.openURL(url).catch(() => {});
    }
  }, []);

  const handleImagePress = useCallback((url: string) => {
    if (onImagePress) {
      const index = imageUrls.indexOf(url);
      onImagePress(imageUrls, index >= 0 ? index : 0);
    } else {
      fallbackOpen(url);
    }
  }, [onImagePress, imageUrls, fallbackOpen]);

  const handleDocumentPress = useCallback((url: string, name: string) => {
    if (onDocumentPress) {
      onDocumentPress(url, name);
    } else {
      fallbackOpen(url);
    }
  }, [onDocumentPress, fallbackOpen]);

  if (!attachments || attachments.length === 0) return null;

  return (
    <View style={styles.container}>
      {attachments.map((attachment) =>
        isImageAttachment(attachment) ? (
          <TouchableOpacity
            key={attachment.id}
            style={styles.imageWrapper}
            onPress={() => handleImagePress(attachment.fileUrl)}
            activeOpacity={0.8}
          >
            <Image source={{ uri: attachment.fileUrl }} style={styles.image} resizeMode="cover" />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            key={attachment.id}
            style={[styles.fileWrapper, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}
            onPress={() => handleDocumentPress(attachment.fileUrl, attachment.fileName)}
            activeOpacity={0.7}
          >
            <RESIDENTS_SVG_COMPONENTS.DOCUMENT width={18} height={18} color={colors.text.muted} />
            <View style={styles.fileInfo}>
              <Text style={[styles.fileName, { color: colors.text.primary }]} numberOfLines={1}>{attachment.fileName}</Text>
              {attachment.fileSize ? (
                <Text style={[styles.fileSize, { color: colors.text.muted }]}>{formatFileSize(attachment.fileSize)}</Text>
              ) : null}
            </View>
          </TouchableOpacity>
        ),
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  imageWrapper: {
    width: 100,
    height: 100,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: PRIMITIVE_COLORS.gray850,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  fileWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    width: '100%',
  },
  fileInfo: {
    flex: 1,
    gap: 2,
  },
  fileName: {
    ...REDESIGN_TYPOGRAPHY.small,
    fontSize: 14,
  },
  fileSize: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
});
